import React, { useContext, useEffect, useState } from "react"; // eslint-disable-line no-unused-vars
import { useMsal } from "@azure/msal-react";
import { ModelCxContext } from "../context/modelCx.context";
import { AppContext, companies } from "../context/app.context";
import Dropdown from "./Dropdown";
import contrastDark from "./../assets/icons/contrastDark.svg";
import contrastLight from "./../assets/icons/contrastLight.svg";
import accountCircle from "./../assets/icons/accountCircle.svg";
import check from "./../assets/icons/check.svg";
import logoBigDark from "./../assets/Logo-KI-big-dark.svg";
import logoBig from "./../assets/Logo-KI-big.png";
import { APP_VERSION } from "./../utility/keys";
import { canAccessTimeMachine } from "./../utility/canAccessTimeMachine";
import { canAccessDashboard } from "./../utility/canAccessDashboard";
import { canAccessCompass } from "./../utility/canAccessCompass";
import { canAccessTicketMaster } from "./../utility/canAccessTicketMaster";
import { canAccessLogs } from "./../utility/canAccessLogs";
import { isCapacityAwareEnabled } from "./../utility/isCapacityAwareEnabled";
import TimeMachineButton from "./TimeMachineButton";
import DashboardButton from "./DashboardButton";
import CompassButton from "./CompassButton";
import TicketMasterButton from "./TicketMasterButton";

const Header = ({ onOpenTimeMachinePicker }) => {
  const { mongoUser, tsUserDocument } = useContext(ModelCxContext);
  // #247 — the Logs button now opens the CloudWatch log browser, which reads
  // AWS Lambda logs through the queries Lambda regardless of which backend the
  // user is on. So it is gated ONLY by the "logs" screenAccess token; the old
  // !isConvexOnly hide belonged to the retired Realm→TanStack comparison tool
  // (whose data was never mirrored to Convex) and no longer applies.
  const {
    appEnvirons,
    companyCode,
    whichList,
    darkMode,
    screenAccess,
    setCompanyCode,
    setWhichList,
    setDarkMode,
    setOpenJobSettingsModal,
    setOpenHelpModal,
    setOpenResetTutorialConfirm,
  } = useContext(AppContext);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const { instance } = useMsal();

  // userAcronym computation (mongoUser?.profile?.name → initials) was
  // removed — that field stopped being populated after the Realm → TanStack
  // migration (#105 era), leaving the trigger button blank. Replaced with
  // a generic account icon SVG below.

  // - - - - - - - - - -

  const selectCompanyHandler = (code) => {
    setCompanyCode(code);
  };

  // - - - - - - - - - -

  const onLogout = () => {
    instance
      .logoutPopup({
        account: instance.getActiveAccount(),
      })
      .catch((error) => {
        console.log(
          "Header onLogout handleLoginError (alert to user 'Logout failed'):",
          error
        );
        alert("Logout failed!");
      });
  };

  // - - - - - - - - - - - - - - - - -  EFFECT HOOKS  - - - - - - - - - - - - - - - - - -
  //
  // companyCode
  // darkMode
  //

  useEffect(() => {
    const initialCompany = companies.find(
      (company) => company.code === companyCode
    );
    setSelectedCompany(initialCompany);
  }, [companyCode]);

  // - - - - - - - - - -

  useEffect(() => {
    localStorage.setItem("darkMode", JSON.stringify(darkMode));

    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [darkMode]);

  // - - - - - - - - - -

  return (
    <header className="h-16 min-h-16 px-4 flex items-center bg-skin-primary border-b-2 border-yellow">
      {mongoUser ? (
        <>
          <div className="w-56">
            {selectedCompany ? (
              <Dropdown
                trigger={
                  <div className="w-56 flex items-center">
                    <img
                      src={selectedCompany.logo}
                      alt="logo"
                      className="w-10"
                    />
                    <span className="flex-1 text-xl font-normal ml-2 whitespace-nowrap">
                      {selectedCompany.name}
                    </span>
                    <span className="ml-2 arrow arrow-down"></span>
                  </div>
                }
                menuClasses="-left-4 translate-y-3 w-64"
                menu={
                  <ul className="text-sm font-normal whitespace-nowrap">
                    {companies.map(({ name, logo, code }) => (
                      <li
                        onClick={() => selectCompanyHandler(code)}
                        key={code}
                        className="px-4 py-2 flex items-center cursor-default hover:bg-skin-base-hover"
                      >
                        <img src={logo} alt="logo" className="w-10" />
                        <span className="flex-1 text-xl ml-1 overflow-hidden text-ellipsis">
                          {name}
                        </span>
                        <img
                          src={check}
                          alt="selected"
                          className={companyCode === code ? "" : "hidden"}
                        />
                      </li>
                    ))}
                  </ul>
                }
              ></Dropdown>
            ) : (
              ""
            )}
          </div>
          {/* #130 (2026-05-21) — priority-ordered responsive header.
              Per Leland's 2026-05-21 spec, the priority order is:
                1. Version stamp — HIGH (debugging + helpdesk identification;
                   always visible at all viewport widths)
                2. "| Jobs Scheduler" app title — MEDIUM (kept visible at
                   all widths; responsive font sizing prevents overflow.
                   `truncate` + `min-w-0` permit graceful clipping if a
                   user gets to a narrow-enough viewport)
                3. Env badge — MEDIUM (always shown in short form
                   "DEV SRV" / "UAT SRV"; production hides the badge
                   entirely so only the version stamp shows. No long-form
                   expansion at any width — the breakpoint jump from short
                   → long caused overflow that hid the version behind the
                   tab strip.)
              Tab strip + right-side icon row sizing handled below. */}
          <div className="flex-1 min-w-0 ml-4 text-base xl:text-xl 2xl:text-2xl font-light truncate">
            |<span className="font-bold ml-2 xl:ml-4">Jobs Scheduler</span>
          </div>
        </>
      ) : (
        <div className="h-10 w-[45%]">
          {darkMode ? (
            <img className="h-full" src={logoBig} alt="Keller" />
          ) : (
            <img className="h-full" src={logoBigDark} alt="Keller" />
          )}
        </div>
      )}

      <div className="flex-1 min-w-0 ml-4 flex items-center gap-2">
        {appEnvirons.appEnv !== "production" && (
          <span className="font-bold whitespace-nowrap text-sm uppercase tracking-wide">
            {appEnvirons.appEnv === "development"
              ? "DEV"
              : appEnvirons.appEnv === "uat"
              ? "UAT"
              : appEnvirons.appEnv.toUpperCase()}{" "}
            SRV
          </span>
        )}
        <span
          className="text-sm font-normal text-skin-muted whitespace-nowrap"
          title="App Version"
        >
          v{APP_VERSION}
        </span>
      </div>
      {mongoUser ? (
        <>
          {screenAccess?.includes("optimizer") ? (
            <button
              className={`${
                whichList === "optimizer"
                  ? "text-skin-primary bg-skin-base"
                  : "text-skin-link"
              } text-sm xl:text-base 2xl:text-lg font-bold px-2 xl:px-3 2xl:px-4 h-full`}
              onClick={() => setWhichList("optimizer")}
              data-tutorial-target="optimizer-tab"
            >
              Optimizer
            </button>
          ) : (
            <></>
          )}
          {screenAccess?.includes("scheduler") ? (
            <button
              className={`${
                whichList === "schedule"
                  ? "text-skin-primary bg-skin-base"
                  : "text-skin-link"
              } text-sm xl:text-base 2xl:text-lg font-bold px-2 xl:px-3 2xl:px-4 h-full`}
              onClick={() => setWhichList("schedule")}
            >
              Scheduler
            </button>
          ) : (
            <></>
          )}
          {screenAccess?.includes("jobList") ? (
            <button
              className={`${
                whichList === "jobList"
                  ? "text-skin-primary bg-skin-base"
                  : "text-skin-link"
              } text-sm xl:text-base 2xl:text-lg font-bold px-2 xl:px-3 2xl:px-4 h-full`}
              onClick={() => setWhichList("jobList")}
            >
              Jobs
            </button>
          ) : (
            <></>
          )}
        </>
      ) : (
        ""
      )}
      <span className="w-10"></span>
      {mongoUser ? (
        <button
          onClick={() => setOpenHelpModal({ show: true })}
          type="button"
          title="Leela's Training Videos"
          className="w-7 h-7 ml-4 rounded-full border-2 border-skin-link text-skin-link font-bold leading-none flex items-center justify-center hover:opacity-75"
        >
          ?
        </button>
      ) : (
        ""
      )}
      {canAccessTimeMachine(mongoUser, tsUserDocument) ? (
        <TimeMachineButton onClick={onOpenTimeMachinePicker} />
      ) : (
        ""
      )}
      {/* #253 — Next Day Openings is capacity-data-driven; hidden for SCG (which
          doesn't carry capacity fields yet), same as every other capacity gate. */}
      {canAccessDashboard(mongoUser, tsUserDocument) &&
      isCapacityAwareEnabled(companyCode) ? (
        <DashboardButton />
      ) : (
        ""
      )}
      {canAccessCompass(mongoUser, tsUserDocument) ? <CompassButton /> : ""}
      {canAccessTicketMaster(mongoUser, tsUserDocument) ? (
        <TicketMasterButton />
      ) : (
        ""
      )}
      {/* #(2026-06-12) Logs + Users moved out of the text-tab strip into the
          circle-icon cluster, matching the Help / Time Machine / Dashboard /
          Compass buttons. They remain view tabs (set whichList) with an
          accent-filled active state. Gated by screenAccess as before. */}
      {canAccessLogs(mongoUser, tsUserDocument) ? (
        <button
          onClick={() => setWhichList("logs")}
          type="button"
          title="Logs"
          aria-label="Logs"
          className="w-7 h-7 ml-4 rounded-full border-2 border-skin-link text-skin-link leading-none flex items-center justify-center hover:opacity-75"
          style={
            whichList === "logs"
              ? {
                  backgroundColor: "rgb(var(--color-button-accent))",
                  borderColor: "rgb(var(--color-button-accent))",
                  color: "#000",
                }
              : undefined
          }
        >
          <span aria-hidden="true">📋</span>
        </button>
      ) : (
        ""
      )}
      {mongoUser && screenAccess?.includes("userList") ? (
        <button
          onClick={() => setWhichList("userList")}
          type="button"
          title="Users"
          aria-label="Users"
          className="w-7 h-7 ml-4 rounded-full border-2 border-skin-link text-skin-link leading-none flex items-center justify-center hover:opacity-75"
          style={
            whichList === "userList"
              ? {
                  backgroundColor: "rgb(var(--color-button-accent))",
                  borderColor: "rgb(var(--color-button-accent))",
                  color: "#000",
                }
              : undefined
          }
        >
          <span aria-hidden="true">👥</span>
        </button>
      ) : (
        ""
      )}
      <button
        onClick={() => setDarkMode(!darkMode)}
        type="button"
        className="w-6 h-6 ml-4"
      >
        {darkMode ? (
          <img src={contrastDark} alt="dark" />
        ) : (
          <img src={contrastLight} alt="light" />
        )}
      </button>
      {mongoUser ? (
        <div className="ml-4">
          <Dropdown
            trigger={
              <div className="w-10 h-10 rounded-full bg-skin-button-accent text-black flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-6 h-6"
                  aria-label="Account"
                >
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </div>
            }
            menuClasses="right-0 translate-y-3"
            menu={
              <ul className="text-sm font-normal whitespace-nowrap">
                <li
                  onClick={(event) => event.stopPropagation()}
                  className="flex px-4 py-2"
                  title={mongoUser.profile?.name}
                >
                  <img src={accountCircle} alt="Account" />
                  <span className="text-base ml-1 overflow-hidden text-ellipsis">
                    {mongoUser.profile?.name}
                  </span>
                </li>
                <li className="px-4 py-2 cursor-default hover:bg-skin-base-hover">
                  Profile Settings
                </li>
                <li
                  className="px-4 py-2 cursor-default hover:bg-skin-base-hover"
                  onClick={() =>
                    setOpenJobSettingsModal({
                      show: true,
                    })
                  }
                >
                  Job Settings
                </li>
                {canAccessTimeMachine(mongoUser, tsUserDocument) ? (
                  <li
                    className="px-4 py-2 cursor-default hover:bg-skin-base-hover"
                    onClick={() =>
                      setOpenResetTutorialConfirm({ show: true })
                    }
                  >
                    Reset Time Machine Tutorial
                  </li>
                ) : null}
                <li
                  onClick={onLogout}
                  className="px-4 py-2 cursor-default hover:bg-skin-base-hover"
                >
                  Log Out
                </li>
              </ul>
            }
          ></Dropdown>
        </div>
      ) : (
        ""
      )}
    </header>
  );
};

export default Header;
